import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BroadcastSetupReason,
  GDPRConfig,
  InitRoomPayload,
  RoomErrorPayload,
  RoomState,
  RoomStatus,
  RoomTokenData,
  type ValidateRoomPayload,
} from 'features/room/types';
import { SDKConnected } from 'features/sdk/sdkStateSlice';
import { RootState } from 'store/store';
import { translateBreakoutDefaultRoomName } from 'features/breakout-rooms/utils/translateBreakoutDefaultRoomName';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import {
  sessionEnded,
  sessionLeft,
  signalingEntryAccessAwaitingStarted,
  signalingRoomJoined,
  signalingRoomJoinPaused,
  signalingRoomLocked,
  signalingRoomUnlocked,
  signalingSessionAwaitingStarted,
} from 'features/room/actions';

export const initialState: RoomState = {
  private: false,
  lastId: '',
  id: '',
  urlId: '',
  status: 'initializing',
  error: {
    global: false,
    name: '',
    message: '',
  },
  token: {},
  joinScreenEnabled: true,
  logoEnabled: false,
  logoUrl: '',
  broadcastSetup: { reason: null, active: false },
  awaitingJoinReason: null,
  consentConfig: null,
  locked: false,
  entryProgress: null,
  loginSubmitted: false,
  loginFailed: false,
  loginError: undefined,
  broadcasterLimit: 1,
  requireRemoveUserConfirmation: true,
  rejoinSessionEnabled: false,
  localMuteEnabled: false,
  lobbyJoinSoundEnabled: false,
  validated: false,
  audioOnly: false,
  autoPictureInPictureEnabled: false,
  inviteAdvancedEnabled: true,
  pinPanelsEnabled: false,
  watermark: {
    enabled: false,
  },
};

export const roomSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    initRoomStarted(state, action: PayloadAction<InitRoomPayload>) {
      const { urlId, token } = action.payload;
      state.urlId = urlId;
      if (token) {
        state.token.jwt = token;
      }
    },
    roomStatusUpdated(state, action: PayloadAction<RoomStatus>) {
      state.status = action.payload;
    },
    roomJoinStarted(state) {
      state.entryProgress = 'joining';
    },
    loginStarted(state) {
      state.entryProgress = 'authorizing';
      state.loginFailed = false;
      state.loginError = undefined;
    },
    loginFinished(state) {
      state.entryProgress = null;
      state.loginSubmitted = true;
      state.loginFailed = false;
      state.status = 'join';
    },
    loginFailed(state, action: PayloadAction<string | undefined>) {
      state.entryProgress = null;
      state.loginSubmitted = true;
      state.loginFailed = true;

      if (action.payload) {
        state.loginError = action.payload;
      }
    },
    roomJoined(state) {
      state.status = 'joined';
      state.entryProgress = null;
      state.consentConfig = null;
    },
    roomTokenParsed(state, action: PayloadAction<RoomTokenData>) {
      state.token.parsedData = action.payload;
    },
    roomError(state, action: PayloadAction<RoomErrorPayload | undefined>) {
      state.status = 'error';
      if (action.payload) {
        state.error.name = action.payload.name;
        state.error.title = action.payload.title;
        state.error.message = action.payload.message;

        if (action.payload.global) {
          state.error.global = action.payload.global;
        }
      }
    },
    broadcastSetupStarted(
      state,
      action: PayloadAction<{ reason: Exclude<BroadcastSetupReason, null> }>
    ) {
      state.broadcastSetup.active = true;
      state.broadcastSetup.reason = action.payload.reason;
    },
    broadcastSetupEnded(state) {
      state.broadcastSetup.active = false;
      state.broadcastSetup.reason = null;
    },
    consentConfigReceived(state, action: PayloadAction<GDPRConfig>) {
      // [fun stuff]
      const linkTag = action.payload.message.match(/(\[link).+(\[\/link\])/)?.[0];

      if (linkTag) {
        // ugly regexes because sfafari doesn't support lookbehind operator until the very recent version.
        const linkText = linkTag.match(/(?:\]).+?(?=\[)/)?.[0].slice(1);
        const linkHref = linkTag.match(/(?:\[link ).+?(?=\])/)?.[0].split(' ')?.[1];

        if (linkHref && linkText) {
          action.payload.message = action.payload.message.replace(
            linkTag,
            `<a href="${linkHref}" target="_blank" rel="noopener nofollow">${linkText}</a>`
          );
        }
      }
      // [/fun stuff]

      state.consentConfig = action.payload;
    },
    sessionDuplicationDetected(state) {
      state.status = 'duplication-detected';
    },
    sessionIntercepted(state) {
      state.status = 'initializing';
    },
    lobbyJoinSoundChanged(state, action: PayloadAction<boolean>) {
      state.lobbyJoinSoundEnabled = action.payload;
    },
    joinScreenEnabledChanged(state, action: PayloadAction<boolean>) {
      state.joinScreenEnabled = action.payload;
    },
    roomValidated(state, action: PayloadAction<ValidateRoomPayload>) {
      state.validated = true;
      state.audioOnly = action.payload.audioOnly;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authorizeUser.fulfilled, (state, action) => {
        const { settings } = action.payload;

        state.logoEnabled = settings.logoEnabled;
        state.logoUrl = settings.logoUrl;
        state.rejoinSessionEnabled = settings.rejoinSessionEnabled;
        state.localMuteEnabled = settings.localMuteEnabled;
        state.autoPictureInPictureEnabled = settings.autoPictureInPictureEnabled;
        state.inviteAdvancedEnabled = settings.inviteAdvancedEnabled;
        state.pinPanelsEnabled = settings.pinPanelsEnabled;

        if (settings.watermark) {
          state.watermark = {
            enabled: settings.watermark.enabled,
            text: settings.watermark.text,
          };
        }

        state.private = action.payload.private;

        state.lobbyMessage = settings.lobbyMessage;
        state.lobbyJoinSoundEnabled = settings.lobbySoundEnabled;
      })
      .addCase(sessionEnded, (state) => {
        state.status = 'ended';
      })
      .addCase(signalingSessionAwaitingStarted, (state) => {
        state.awaitingJoinReason = 'session-start';
      })
      .addCase(signalingEntryAccessAwaitingStarted, (state) => {
        state.awaitingJoinReason = 'entry-access';
      })
      .addCase(signalingRoomJoinPaused, (state) => {
        state.awaitingJoinReason = 'scale-server-boot';
      })
      .addCase(signalingRoomJoined, (state, action) => {
        state.awaitingJoinReason = null;

        state.lastId = state.id;
        state.id = action.payload.roomId;

        state.locked = action.payload.roomLocked;
        state.broadcasterLimit = action.payload.broadcastersLimit;
        state.name = action.payload.roomName;
      })
      .addCase(sessionLeft, (state) => {
        state.status = 'left';
      })
      .addCase(signalingRoomLocked, (state) => {
        state.locked = true;
      })
      .addCase(signalingRoomUnlocked, (state) => {
        state.locked = false;
      })
      .addCase(SDKConnected, (state, action) => {
        if (action.payload.requireRemoveUserConfirmation !== undefined) {
          state.requireRemoveUserConfirmation = action.payload.requireRemoveUserConfirmation;
        }
      });
  },
});

export const {
  initRoomStarted,
  roomStatusUpdated,
  roomJoinStarted,
  roomJoined,
  roomError,
  roomTokenParsed,
  broadcastSetupStarted,
  broadcastSetupEnded,
  consentConfigReceived,
  loginFinished,
  loginStarted,
  loginFailed,
  sessionDuplicationDetected,
  sessionIntercepted,
  lobbyJoinSoundChanged,
  roomValidated,
  joinScreenEnabledChanged,
} = roomSlice.actions;

export const selectRoomId = (state: RootState) => state.room.id;

export const selectRoomUrlId = (state: RootState) => state.room.urlId;
export const selectRoomLastId = (state: RootState) => state.room.lastId;

export const selectIsRoomPublic = (state: RootState) => !state.room.private;

export const selectRoomName = (state: RootState) =>
  state.room.name ? translateBreakoutDefaultRoomName(state.room.name) : state.room.name;

export const selectRoomStatus = (state: RootState) => state.room.status;
export const selectRoomError = (state: RootState) => state.room.error;
export const selectRoomEntryProgress = (state: RootState) => state.room.entryProgress;
export const selectRoomToken = (state: RootState) => state.room.token;

export const selectRoomLogoEnabled = (state: RootState) => state.room.logoEnabled;
export const selectJoinScreenEnabled = (state: RootState) => state.room.joinScreenEnabled;

export const selectIsBroadcastSetupActive = (state: RootState) => state.room.broadcastSetup.active;

export const selectAwaitingRoomJoin = (state: RootState) => state.room.awaitingJoinReason;

export const selectCustomLogoUrl = (state: RootState) => state.room.logoUrl;

export const selectConsentConfig = (state: RootState) => state.room.consentConfig;
export const selectRoomLocked = (state: RootState) => state.room.locked;

export const selectBroadcasterLimit = (state: RootState) => state.room.broadcasterLimit;

export const selectBroadcastSetupReason = (state: RootState) => state.room.broadcastSetup.reason;

export const selectRequireRemoveUserConfirmation = (state: RootState) =>
  state.room.requireRemoveUserConfirmation;

export const selectLoginSubmitted = (state: RootState) => state.room.loginSubmitted;

export const selectLoginFailed = (state: RootState) => state.room.loginFailed;

export const selectLoginError = (state: RootState) => state.room.loginError;

export const selectRejoinEnabled = (state: RootState) => state.room.rejoinSessionEnabled;

export const selectLocalMuteEnabled = (state: RootState) => state.room.localMuteEnabled;

export const selectLobbyMessage = (state: RootState) => state.room.lobbyMessage;

export const selectLobbyJoinSoundEnabled = (state: RootState) => state.room.lobbyJoinSoundEnabled;

export const selectRoomValidated = (state: RootState) => state.room.validated;

export const selectIsAudioOnlyRoom = (state: RootState) => state.room.audioOnly;

export const selectAutoPictureInPictureEnabled = (state: RootState) =>
  state.room.autoPictureInPictureEnabled;

export const selectInviteAdvancedEnabled = (state: RootState) => state.room.inviteAdvancedEnabled;

export const selectPinPanelsEnabled = (state: RootState) => state.room.pinPanelsEnabled;

export const selectWatermarkEnabled = (state: RootState) => state.room.watermark.enabled;

export const selectWatermarkText = (state: RootState) => state.room.watermark.text;

export default roomSlice.reducer;
